import React, { useEffect, useState, useMemo } from "react";
import "./TrialEndScreen.css";

/**
 * TrialEndScreen – High-converting post-trial paywall
 *
 * Props
 * ─────
 *  previewScores  { communication: "7/10", confidence: "8/10", summary: "…" }
 *  onUpgrade      () => void   – navigate to payment / pricing
 *  onRetry        () => void   – start another free trial interview
 */
export default function TrialEndScreen({ previewScores, onUpgrade, onRetry }) {
  /* ---------- parse targets ---------- */
  const parse = (s) => {
    if (!s) return 0;
    const n = parseInt(s, 10);
    return isNaN(n) ? 0 : Math.min(n, 10);
  };

  const commTarget = parse(previewScores?.communication);
  const confTarget = parse(previewScores?.confidence);

  /* ---------- animated counters ---------- */
  const [animComm, setAnimComm] = useState(0);
  const [animConf, setAnimConf] = useState(0);
  const [animDone, setAnimDone] = useState(false);

  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const dur = 1400;

    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setAnimComm(Math.round(commTarget * ease));
      setAnimConf(Math.round(confTarget * ease));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setAnimDone(true);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [commTarget, confTarget]);

  /* ---------- confetti (when high scores + anim done) ---------- */
  const showConfetti = animDone && (commTarget >= 7 || confTarget >= 7);

  const confettiPieces = useMemo(() => {
    const colors = ["#667eea", "#4ECDC4", "#ff6b6b", "#ffd93d", "#a78bfa", "#ff9a9e"];
    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      bg: colors[i % colors.length],
      delay: `${Math.random() * 0.8}s`,
      duration: `${1.6 + Math.random() * 1.2}s`,
      size: `${6 + Math.random() * 6}px`,
      rotate: `${Math.random() * 360}deg`,
    }));
  }, []);

  /* ---------- SVG ring helpers ---------- */
  const R = 36;
  const C = 2 * Math.PI * R; // ≈ 226.19

  const ringDash = (val) => `${(val / 10) * C} ${C}`;

  return (
    <div className="tes-overlay">
      {/* Animated background blobs */}
      <div className="tes-bg-blobs" aria-hidden="true">
        <div className="tes-blob tes-blob-1" />
        <div className="tes-blob tes-blob-2" />
        <div className="tes-blob tes-blob-3" />
      </div>

      <div className="tes-card">

        {/* Confetti */}
        {showConfetti && (
          <div className="tes-confetti-container" aria-hidden="true">
            {confettiPieces.map((p) => (
              <span
                key={p.id}
                className="tes-confetti-piece"
                style={{
                  left: p.left,
                  background: p.bg,
                  width: p.size,
                  height: p.size,
                  animationDelay: p.delay,
                  animationDuration: p.duration,
                  transform: `rotate(${p.rotate})`,
                }}
              />
            ))}
          </div>
        )}

        {/* ---- Brand header ---- */}
        <div className="tes-brand-header">
          <img src="/Vagmee_Logo.png" alt="Vagmee" className="tes-brand-logo" />
          <span className="tes-brand-name">Vagmee</span>
        </div>

        {/* ---- Header ---- */}
        <div className="tes-emoji" role="img" aria-label="target">🎯</div>
        <h2 className="tes-title">Great start!</h2>
        <p className="tes-subtitle">
          Here's a sneak peek of how you did in just 2 questions
        </p>

        {/* ---- Animated Score Rings ---- */}
        <div className="tes-scores">
          {/* Communication */}
          <div className="tes-score-item">
            <svg className="tes-ring-svg" viewBox="0 0 88 88">
              <circle className="tes-ring-bg" cx="44" cy="44" r={R} />
              <circle
                className="tes-ring-fg tes-ring-fg--comm"
                cx="44" cy="44" r={R}
                strokeDasharray={ringDash(animComm)}
              />
              <text className="tes-ring-value" x="44" y="41">
                {animComm}
              </text>
              <text className="tes-ring-max" x="44" y="56" textAnchor="middle">
                / 10
              </text>
            </svg>
            <span className="tes-score-label">Communication</span>
          </div>

          {/* Confidence */}
          <div className="tes-score-item">
            <svg className="tes-ring-svg" viewBox="0 0 88 88">
              <circle className="tes-ring-bg" cx="44" cy="44" r={R} />
              <circle
                className="tes-ring-fg tes-ring-fg--conf"
                cx="44" cy="44" r={R}
                strokeDasharray={ringDash(animConf)}
              />
              <text className="tes-ring-value" x="44" y="41">
                {animConf}
              </text>
              <text className="tes-ring-max" x="44" y="56" textAnchor="middle">
                / 10
              </text>
            </svg>
            <span className="tes-score-label">Confidence</span>
          </div>
        </div>

        {/* ---- Summary ---- */}
        {previewScores?.summary && (
          <p className="tes-summary">"{previewScores.summary}"</p>
        )}

        {/* ---- Blurred Report Preview ---- */}
        <div className="tes-blur-panel">
          <div className="tes-blur-inner">
            <div className="tes-blur-row">
              <span>Technical Knowledge</span>
              <span>██ / 100</span>
            </div>
            <div className="tes-blur-row">
              <span>Problem Solving</span>
              <span>██ / 100</span>
            </div>
            <div className="tes-blur-row">
              <span>Interview Readiness</span>
              <span>██ / 100</span>
            </div>
            <div className="tes-blur-row">
              <span>Top Strengths</span>
              <span>████████</span>
            </div>
            <div className="tes-blur-row">
              <span>Improvement Plan</span>
              <span>████████</span>
            </div>
          </div>
          <div className="tes-blur-overlay">
            <span className="tes-lock-icon">🔒</span>
            <span className="tes-lock-text">Full Report</span>
          </div>
        </div>

        {/* ---- Urgency ---- */}
        <div className="tes-urgency">
          <span className="tes-urgency-badge">50% OFF</span>
          <span className="tes-urgency-text">
            Trial offer — <strong>valid for 2 days only</strong>
          </span>
        </div>

        {/* ---- Primary CTA ---- */}
        <button className="tes-cta-primary" onClick={onUpgrade}>
          Get My Full Report — 50% Off
        </button>

        {/* ---- Secondary CTA ---- */}
        <button className="tes-cta-secondary" onClick={onRetry}>
          Try Another Free Interview →
        </button>

        {/* ---- Trust badge ---- */}
        <p className="tes-trust-badge">
          🔒 Secured by <strong>Razorpay</strong> · 256-bit SSL · UPI / Cards / Netbanking
        </p>
      </div>
    </div>
  );
}
